import React from 'react';
import { X, Gem } from 'lucide-react';
import { motion } from 'framer-motion';
import { Commission } from '../types/commission';
interface EmployeeDetailPanelProps {
  commission: Commission;
  onClose: () => void;
}
function getTierColor(units: number): {
  bg: string;
  text: string;
  border: string;
} {
  if (units >= 9) {
    return {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200'
    };
  } else if (units >= 4) {
    return {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200'
    };
  } else {
    return {
      bg: 'bg-slate-50',
      text: 'text-slate-700',
      border: 'border-slate-200'
    };
  }
}
function getBadgeColor(badge: string): {
  bg: string;
  text: string;
  border: string;
} {
  const colors = {
    Diamond: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200'
    },
    Platinum: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200'
    },
    Gold: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-200'
    }
  };
  return colors[badge as keyof typeof colors] || colors.Gold;
}
function getTierName(units: number): string {
  if (units >= 9) return '9+ Units';
  if (units >= 4) return '4-8 Units';
  return '1-3 Units';
}
export function EmployeeDetailPanel({
  commission,
  onClose
}: EmployeeDetailPanelProps) {
  const tierName = getTierName(commission.units);
  const tierColors = getTierColor(commission.units);
  const totalLoanVolume = commission.loans.reduce((acc, loan) => acc + loan.amount, 0);
  const badge = commission.profile?.badge || 'Gold';
  const badgeColors = getBadgeColor(badge);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-end" onClick={onClose} data-id="element-334">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} onClick={e => e.stopPropagation()} className="bg-white h-full w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-335">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0" data-id="element-336">
          <div className="flex items-center justify-between mb-4" data-id="element-337">
            <div className="flex items-center gap-3" data-id="element-338">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold" data-id="element-339">
                {commission.userName.split(' ').map(n => n[0]).join('')}
              </div>
              <div data-id="element-340">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-341">
                  {commission.userName}
                </h2>
                <p className="text-sm text-slate-600" data-id="element-342">
                  {commission.userProfile}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-343">
              <X size={24} className="text-slate-500" data-id="element-344" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-3" data-id="element-345">
            <div className="bg-slate-50 rounded-lg p-3" data-id="element-346">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-347">
                Period
              </p>
              <p className="text-sm font-bold text-slate-900" data-id="element-348">
                {commission.payPeriod}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3" data-id="element-349">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-350">
                Units
              </p>
              <p className="text-sm font-bold text-slate-900" data-id="element-351">
                {commission.units}
              </p>
            </div>
            <div className={`rounded-lg p-3 border ${tierColors.border} ${tierColors.bg}`} data-id="element-352">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-353">
                Current Tier
              </p>
              <p className={`text-sm font-bold ${tierColors.text}`} data-id="element-354">
                {tierName}
              </p>
            </div>
            <div className={`rounded-lg p-3 border ${badgeColors.border} ${badgeColors.bg}`} data-id="element-355">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-356">
                Current Badge
              </p>
              <p className={`text-sm font-bold ${badgeColors.text}`} data-id="element-357">{badge}</p>
            </div>
          </div>
        </div>

        {/* Loans List */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-358">
          <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-359">
            Loan Details
          </h3>

          {commission.loans.length > 0 ? <div className="space-y-3" data-id="element-360">
              {commission.loans.map(loan => <div key={loan.loanId} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-361">
                  <div className="flex items-center justify-between mb-2" data-id="element-362">
                    <span className="text-sm font-bold text-slate-900" data-id="element-363">
                      {loan.loanId}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-364">{loan.date}</span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-365">
                    <div data-id="element-366">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-367">Loan Amount</p>
                      <p className="text-sm font-semibold text-slate-900 tabular-nums" data-id="element-368">
                        $
                        {loan.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-369">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-370">Commission</p>
                      <p className="text-sm font-bold text-emerald-600 tabular-nums" data-id="element-371">
                        $
                        {loan.commission.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-12" data-id="element-372">
              <p className="text-slate-500" data-id="element-373">No loans for this period</p>
            </div>}
        </div>

        {/* Footer Totals */}
        <div className="p-6 border-t-2 border-slate-300 bg-gradient-to-r from-slate-50 to-slate-100 flex-shrink-0" data-id="element-374">
          <div className="flex items-center justify-between" data-id="element-375">
            <div data-id="element-376">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-377">
                Total Loan Volume
              </p>
              <p className="text-xl font-bold text-slate-900 tabular-nums" data-id="element-378">
                $
                {totalLoanVolume.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="text-right" data-id="element-379">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-380">
                Total Commission
              </p>
              <p className="text-xl font-bold text-emerald-700 tabular-nums" data-id="element-381">
                $
                {commission.total.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}